<template>
  <AbstractIdeaTaskModalComponent :action="action" :event="event" :disabled="disabled">
    <template #description>
      <EventActionFieldRowComponent :label="t('components.TaskModal.idea.veto')">
        <div class="mx-0 flex flex-wrap-nowrap">
          <div class="col-6" style="padding: 0 0.5em 0 0">
            <LISAButton
              class="w-100 justify-content-center"
              :class="
                meta.veto === IdeaVeto.noVeto
                  ? 'p-button-success'
                  : 'p-button-outlined p-button-success'
              "
              :disabled="disableAll || originalMeta.veto === IdeaVeto.veto || disabled"
              @click="setVeto(IdeaVeto.noVeto)"
            >
              <i class="fas fa-check mr-1" />
              {{ t("components.TaskModal.idea.noVeto") }}
            </LISAButton>
          </div>
          <div class="col-6" style="padding: 0 0 0 0.5em">
            <LISAButton
              :class="
                meta.veto === IdeaVeto.veto
                  ? 'p-button-danger'
                  : 'p-button-outlined p-button-danger'
              "
              class="w-100 justify-content-center"
              :disabled="disableAll || originalMeta.veto === IdeaVeto.veto || disabled"
              @click="setVeto(IdeaVeto.veto)"
            >
              <i class="fas fa-times mr-1" />
              {{ t("components.TaskModal.idea.veto") }}
            </LISAButton>
          </div>
        </div>
      </EventActionFieldRowComponent>
    </template>
    <slot />
  </AbstractIdeaTaskModalComponent>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { DateTime } from "luxon";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { IdeaVeto, TaskStatus, type EventListEventFields_HLPIdea_Fragment } from "@/graphql";
import AbstractIdeaTaskModalComponent from "./AbstractIdeaTaskModalComponent.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { TaskModalCommentIsRequiredInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import type { EditTaskType } from "@/components/event-action/task/helper/DefaultTask";
import type { EventEditType } from "@/components/event-action/event/modal/EventEditType";

export default defineComponent({
  name: "IdeaTaskModalComponent",
  components: {
    AbstractIdeaTaskModalComponent,
    LISAButton,
    EventActionFieldRowComponent,
  },
  props: {
    action: {
      type: Object as PropType<EditTaskType>,
      required: true,
    },
    event: {
      type: Object as PropType<EventListEventFields_HLPIdea_Fragment | EventEditType>,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup(props) {
    const { t } = useTranslation();
    const originalMeta = ref({ ...props.action.meta } as {
      veto: IdeaVeto;
    });
    function setVeto(veto: IdeaVeto) {
      if (props.action != null) {
        props.action.meta = { veto };
        props.action.status = TaskStatus.act;
      }
    }
    const disableAll = computed(
      () => DateTime.fromISO(props.action.deadline as string) < DateTime.now().startOf("day"),
    );

    const commentIsRequired = injectAsserted(TaskModalCommentIsRequiredInjectionKey);

    const meta = computed(
      () =>
        props.action.meta as {
          veto: IdeaVeto;
        },
    );

    const isCommentRequired = computed(
      () => !disableAll.value && meta.value.veto === IdeaVeto.veto,
    );

    watch(isCommentRequired, (require) => {
      commentIsRequired.value = require;
    });

    return {
      t,
      meta,
      disableAll,
      originalMeta,
      IdeaVeto,
      setVeto,
    };
  },
});
</script>

<style lang="scss" scoped></style>
